import type { AxiosError, AxiosRequestConfig, AxiosRequestHeaders, AxiosResponse } from 'axios';
import axios from 'axios';
import { APPSERVER_API_CONTENT_TYPE } from '@/main/app.constants';
import {
  cancellationRequestInterceptor,
  cancellationResponseErrorInterceptor,
  cancellationResponseInterceptor,
} from '@/requests/interceptors/requestCancellation.interceptor';
import { httpInterceptor } from '@/requests/interceptors/http.interceptor';
import {
  authenticationRequestInterceptor,
  authenticationResponseInterceptor,
} from '@/requests/interceptors/authentication.interceptor';
import { requestOriginInterceptor } from '@/requests/interceptors/requestOrigin.interceptor';
import { sessionIdInterceptor } from '@/requests/interceptors/sessionId.interceptor';
import { handleInterceptError } from '@/requests/interceptors/error.interceptor';
import { versionInterceptor } from '@/requests/interceptors/version.interceptor';
import { conflictRetryInterceptor } from '@/requests/interceptors/retry.interceptor';
import HttpCodes from 'http-status-codes';
import { addRequest, removeRequest } from './axios.actions';
import { sqAxiosStore } from '@/core/core.stores';
import { SeeqNames } from '@/main/app.constants.seeqnames';

const axiosInstance = axios.create({});
axiosInstance.defaults.headers.common['Accept'] = APPSERVER_API_CONTENT_TYPE;

// The order of request interceptors matters, even for the error handling!
// They are executed in REVERSED order: https://github.com/axios/axios/issues/1663#issue-339555912
// So the last one needs to be the `cancellationRequestInterceptor` because it's the one that adds the requestId
axiosInstance.interceptors.request.use(httpInterceptor, handleInterceptError);
axiosInstance.interceptors.request.use(authenticationRequestInterceptor, handleInterceptError);
axiosInstance.interceptors.request.use(requestOriginInterceptor, handleInterceptError);
axiosInstance.interceptors.request.use(sessionIdInterceptor, handleInterceptError);
axiosInstance.interceptors.request.use(incrementPendingRequests, decrementPendingRequestsError);
axiosInstance.interceptors.request.use(cancellationRequestInterceptor, handleInterceptError);

// response interceptor first callback is if status is <=200, second callback to handle errors
axiosInstance.interceptors.response.use((response) => response, authenticationResponseInterceptor);
axiosInstance.interceptors.response.use(versionInterceptor, handleInterceptError);
axiosInstance.interceptors.response.use((response) => response, conflictRetryInterceptor);
axiosInstance.interceptors.response.use((response) => response, handleInterceptError);
axiosInstance.interceptors.response.use(cancellationResponseInterceptor, cancellationResponseErrorInterceptor);
axiosInstance.interceptors.response.use(decrementPendingRequests, decrementPendingRequestsError);

/**
 * Exposes an instance of our properly configured axios-instance to the SDK
 */
export function getAxiosInstance() {
  return axiosInstance;
}

function decrementPendingRequests(response: SeeqAxiosResponse) {
  removeRequest(response.config.headers?.[SeeqNames.API.Headers.RequestId]);
  return Promise.resolve(response);
}

function incrementPendingRequests(config: SeeqInternalAxiosRequestConfig) {
  addRequest(config.headers?.[SeeqNames.API.Headers.RequestId]);
  return config;
}

function decrementPendingRequestsError(error: any) {
  removeRequest(error?.config?.headers?.[SeeqNames.API.Headers.RequestId]);
  return Promise.reject(error);
}

export function getPendingRequestCount() {
  return sqAxiosStore.pendingRequests;
}

/**
 * In order for Notifications to properly display we need to ensure we return a proper Error.
 * This functions extracts a proper Error from the axios error that is returned from http Requests that are
 * unsuccessful.
 */
export function extractError(errorOrResponse: any) {
  if (errorOrResponse?.response?.status >= HttpCodes.BAD_REQUEST) {
    return new Error(errorOrResponse.response.data?.statusMessage || errorOrResponse.response.data);
  }
  if (errorOrResponse.request && errorOrResponse.data?.errors) {
    return new Error(errorOrResponse.data.errors[0].message);
  }
  if (errorOrResponse.request && !errorOrResponse.data && errorOrResponse.message) {
    return new Error(errorOrResponse.message);
  }
  if (errorOrResponse.request && !errorOrResponse.data) {
    return new Error(errorOrResponse.request);
  }
  if (errorOrResponse.config && !errorOrResponse.data) {
    return new Error(errorOrResponse.config);
  }
  return errorOrResponse?.data?.statusMessage || errorOrResponse;
}

/**
 * Determines if the error originated from axios. For some reason the check for isAxiosError is not always reliable.
 *
 * @param error The error
 */
export function isAxiosError(error: unknown): error is AxiosError {
  return axios.isAxiosError(error) || (error as any)?.name === 'AxiosError';
}

export interface SeeqAxiosRequestConfig<D = any> extends AxiosRequestConfig<D> {
  cancellationGroup?: string;
  cancelOnServer?: boolean;
  ignoreLoadingBar?: boolean;
  refetching?: boolean;
}

export interface SeeqInternalAxiosRequestConfig extends SeeqAxiosRequestConfig {
  headers: AxiosRequestHeaders;
}

export interface SeeqAxiosResponse extends AxiosResponse {
  config: SeeqInternalAxiosRequestConfig;
}
